/**
 * Service Due Graph Component
 * Displays service due progress in compact 2x2 grid layout
 * Shows wear accumulation since last maintenance
 */

import React from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Wrench } from 'lucide-react';

const ServiceDueGraph = ({ componentHealth, serviceDueAlarms, vehicleType, themeClasses }) => {
  const { theme, getThemeClasses } = useTheme();
  const classes = themeClasses || getThemeClasses();

  // Component display order and abbreviations
  const components = [
    { key: 'engine', abbr: 'Eng', name: 'Engine' },
    { key: 'suspension', abbr: 'Sus', name: 'Suspension' },
    { key: 'brakes', abbr: 'Brk', name: 'Brakes' },
    { key: 'transmission', abbr: 'Trn', name: 'Transmission' }
  ];

  // Get color class based on alarm level
  const getColorClass = (alarmLevel) => {
    switch (alarmLevel) {
      case 3: return 'bg-red-500'; // Critical
      case 2: return 'bg-orange-500'; // Required
      case 1: return 'bg-yellow-500'; // Recommended
      default: return 'bg-green-500'; // Normal
    }
  };

  // Get status icon based on alarm level
  const getStatusIcon = (alarmLevel) => {
    switch (alarmLevel) {
      case 3: return '🔴';
      case 2: return '🔶';
      case 1: return '⚠️';
      default: return '✅';
    }
  };

  // Check if data exists - only serviceDueAlarms is required
  console.log('🔍 ServiceDueGraph props:', {
    hasComponentHealth: !!componentHealth,
    hasServiceDueAlarms: !!serviceDueAlarms,
    componentHealthType: typeof componentHealth,
    serviceDueAlarmsType: typeof serviceDueAlarms,
    serviceDueAlarmsKeys: serviceDueAlarms ? Object.keys(serviceDueAlarms) : 'null'
  });
  
  if (!serviceDueAlarms || Object.keys(serviceDueAlarms).length === 0) {
    console.log('❌ ServiceDueGraph: Missing serviceDueAlarms data, showing fallback');
    return (
      <div className={`${classes.secondary} rounded-lg p-4 text-center`}>
        <Wrench className={`mx-auto ${classes.textMuted} mb-2`} size={32} />
        <p className={`${classes.textMuted} text-sm`}>
          No service due data available
        </p>
      </div>
    );
  }
  
  console.log('✅ ServiceDueGraph: Rendering with data');

  return (
    <div className={`${classes.secondary} rounded-lg p-4`}>
      <h4 className={`text-sm font-semibold ${classes.textPrimary} mb-3 flex items-center gap-2`}>
        <Wrench size={16} className="text-blue-400" />
        Service Due (Maintenance Cycle)
      </h4>
      
      {/* 2x2 Grid Layout */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {components.map(({ key, abbr, name }) => {
          const alarm = serviceDueAlarms[key];
          const wear = alarm?.wearSinceService || 0;
          const threshold = alarm?.threshold || { max: 100 };
          const alarmLevel = alarm?.alarmLevel || 0;
          const percentOfMax = threshold.max > 0 ? (wear / threshold.max) * 100 : 0;

          return (
            <div key={key} className="flex items-center gap-2">
              {/* Abbreviated name */}
              <div className={`w-8 text-xs font-medium ${classes.textSecondary}`} title={name}>
                {abbr}
              </div>
              
              {/* Progress bar */}
              <div className="flex-1 relative">
                <div className={`h-3 ${theme === 'dark' ? 'bg-gray-700' : 'bg-gray-300'} rounded-full overflow-hidden`}>
                  <div 
                    className={`h-full transition-all duration-300 ${getColorClass(alarmLevel)}`}
                    style={{ width: `${Math.min(percentOfMax, 100)}%` }}
                  />
                </div>
              </div>
              
              {/* Compact percentage */}
              <div className={`text-xs ${classes.textPrimary} font-medium w-16 text-right`}>
                {wear.toFixed(1)}/{threshold.max}%
              </div>
              
              {/* Status icon */}
              <div className="w-5 text-center">
                {getStatusIcon(alarmLevel)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ServiceDueGraph;
